import { DatePicker, Input, InputNumber } from "antd";
import React from "react";
import { useFormik } from "formik";
import dayjs from "dayjs";
import CommonButton from "../common/Button/CommonButton";
import { BackIcon } from "../Icons/BackIcon";
import {
  tournamentGenerateValidationSchema,
  MIN_TOURNAMENT_QUESTIONS,
  MAX_TOURNAMENT_QUESTIONS,
} from "../../utils/validation";

const TournamentGenerateForm = ({
  onSubmit,
  onCancel,
  onSwitchToManual,
  loading = false,
}) => {
  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      ai_instruction: "",
      number_of_questions: 5,
      start_date: null,
      end_date: null,
    },
    validationSchema: tournamentGenerateValidationSchema,
    onSubmit: (values) => {
      onSubmit({
        title: values.title.trim(),
        description: values.description.trim(),
        ai_instruction: values.ai_instruction?.trim() || "",
        number_of_questions: values.number_of_questions,
        start_date: values.start_date
          ? new Date(values.start_date).toISOString()
          : null,
        end_date: values.end_date
          ? new Date(values.end_date).toISOString()
          : null,
      });
    },
  });

  return (
    <div>
      <div className="flex items-center mb-6">
        <span className="cursor-pointer mr-3" onClick={onCancel}>
          <BackIcon />
        </span>
        <h2 className="text-2xl font-semibold font-poppins m-0">
          Create AI Tournament
        </h2>
      </div>

      <p className="text-gray-600 mb-6">
        AI will generate prediction questions from the title, description, and
        extra instructions. Users will later compete against an AI bot using
        these questions.
        {onSwitchToManual && (
          <>
            {" "}
            <span
              className="text-[#4f46e5] cursor-pointer hover:underline"
              onClick={onSwitchToManual}
            >
              Create the whole tournament manually instead
            </span>
            .
          </>
        )}
      </p>

      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g. Premier League Weekend Predictions"
              className="w-full"
              disabled={loading}
            />
            {formik.touched.title && formik.errors.title && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.title}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <Input.TextArea
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Describe the tournament topic and what participants will predict"
              rows={3}
              className="w-full"
              disabled={loading}
            />
            {formik.touched.description && formik.errors.description && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.description}
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional AI Instructions
            </label>
            <Input.TextArea
              name="ai_instruction"
              value={formik.values.ai_instruction}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Optional. e.g. Focus on Premier League matches this weekend, include over/under questions, avoid player injury questions"
              rows={3}
              className="w-full"
              disabled={loading}
            />
            {formik.touched.ai_instruction && formik.errors.ai_instruction && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.ai_instruction}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Questions <span className="text-red-500">*</span>
            </label>
            <InputNumber
              name="number_of_questions"
              min={MIN_TOURNAMENT_QUESTIONS}
              max={MAX_TOURNAMENT_QUESTIONS}
              value={formik.values.number_of_questions}
              onChange={(value) =>
                formik.setFieldValue(
                  "number_of_questions",
                  value ?? MIN_TOURNAMENT_QUESTIONS
                )
              }
              className="w-full"
              style={{ width: "100%" }}
              disabled={loading}
            />
            {formik.touched.number_of_questions &&
              formik.errors.number_of_questions && (
                <p className="text-red-500 text-xs mt-1">
                  {formik.errors.number_of_questions}
                </p>
              )}
          </div>

          <div />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              showTime
              value={
                formik.values.start_date
                  ? dayjs(formik.values.start_date)
                  : null
              }
              onChange={(date) => {
                formik.setFieldValue("start_date", date ? date.toDate() : null);
              }}
              disabledDate={(current) =>
                current && current.isBefore(dayjs().startOf("day"))
              }
              className="w-full"
              format="YYYY-MM-DD HH:mm"
              placeholder="Select start date & time"
              disabled={loading}
            />
            {formik.touched.start_date && formik.errors.start_date && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.start_date}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              showTime
              value={
                formik.values.end_date ? dayjs(formik.values.end_date) : null
              }
              onChange={(date) => {
                formik.setFieldValue("end_date", date ? date.toDate() : null);
              }}
              disabledDate={(current) => {
                const start = formik.values.start_date
                  ? dayjs(formik.values.start_date)
                  : dayjs();
                return current && current.isBefore(start.startOf("day"));
              }}
              className="w-full"
              format="YYYY-MM-DD HH:mm"
              placeholder="Select end date & time"
              disabled={loading}
            />
            {formik.touched.end_date && formik.errors.end_date && (
              <p className="text-red-500 text-xs mt-1">
                {formik.errors.end_date}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <CommonButton
            htmlType="button"
            onClick={onCancel}
            className="rounded-md px-6"
            disabled={loading}
          >
            Cancel
          </CommonButton>
          <CommonButton
            type="primary"
            htmlType="submit"
            disabled={loading}
            className="rounded-md px-6 bg-[#4f46e5] hover:bg-[#4338ca]"
          >
            {loading ? "Generating with AI..." : "Generate Tournament"}
          </CommonButton>
        </div>
      </form>
    </div>
  );
};

export default TournamentGenerateForm;
